import React, { useState } from 'react';
import { recipes } from '../mock';
import { Search, Filter, ArrowRight, Star, Clock, Zap } from 'lucide-react';
import * as LucideIcons from 'lucide-react';

const Recipes = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedComplexity, setSelectedComplexity] = useState('All');

  const categories = ['All', 'Browser', 'Files', 'Apps', 'System', 'Windows', 'Clipboard', 'Spotify'];
  const complexities = ['All', 'Beginner', 'Intermediate', 'Advanced'];

  const filteredRecipes = recipes.filter(recipe => {
    const matchesSearch = recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         recipe.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         recipe.commands.some(cmd => cmd.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || recipe.category === selectedCategory;
    const matchesComplexity = selectedComplexity === 'All' || recipe.complexity === selectedComplexity;
    return matchesSearch && matchesCategory && matchesComplexity;
  });

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName.charAt(0).toUpperCase() + iconName.slice(1)];
    return Icon ? <Icon className="w-6 h-6" /> : null;
  };

  const getComplexityColor = (complexity) => {
    switch(complexity) {
      case 'Beginner': return 'text-green-500 bg-green-500/10 border-green-500/30';
      case 'Intermediate': return 'text-orange-500 bg-orange-500/10 border-orange-500/30';
      case 'Advanced': return 'text-red-500 bg-red-500/10 border-red-500/30';
      default: return 'text-gray-500 bg-gray-500/10 border-gray-500/30';
    }
  };

  const getCategoryStats = () => {
    const stats = {};
    categories.forEach(cat => {
      if (cat === 'All') {
        stats[cat] = recipes.length;
      } else {
        stats[cat] = recipes.filter(r => r.category === cat).length;
      }
    });
    return stats;
  };

  const categoryStats = getCategoryStats();

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 mb-4">
            <Zap className="w-6 h-6 text-orange-500" />
            <span className="text-orange-500 font-semibold uppercase tracking-wider text-sm">
              V3.0 • 34 Automations
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Automation Library
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Pre-built voice commands to supercharge your workflow. Local execution with 300-500ms latency.
          </p>
        </div>

        {/* Real Problems Solved Banner */}
        <div className="mb-12 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-6">
          <h2 className="text-2xl font-bold text-white mb-4 text-center">Real Problems Solved</h2>
          <div className="grid md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-3xl font-bold text-orange-500 mb-1">2+ hrs</p>
              <p className="text-gray-400 text-sm">Saved Daily</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-500 mb-1">80%</p>
              <p className="text-gray-400 text-sm">Less RSI Risk</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-500 mb-1">10 min</p>
              <p className="text-gray-400 text-sm">Saved on Multitasking</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-orange-500 mb-1">100%</p>
              <p className="text-gray-400 text-sm">Voice Control</p>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="mb-12 space-y-6">
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search automations (e.g., 'spotify', 'screenshot', 'browser')..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 bg-zinc-900 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-orange-500/50 focus:ring-2 focus:ring-orange-500/20 transition-all duration-200"
            />
          </div>

          {/* Category Filter */}
          <div>
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Filter className="text-gray-400 w-5 h-5 flex-shrink-0" />
              <span className="text-gray-400 font-semibold">Filter by Category</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    selectedCategory === category
                      ? 'bg-orange-500 text-white scale-105'
                      : 'bg-zinc-900 text-gray-400 hover:bg-zinc-800 border border-white/10'
                  }`}
                >
                  {category}
                  <span className="ml-2 text-xs opacity-75">({categoryStats[category]})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Complexity Filter */}
          <div>
            <div className="flex items-center justify-center space-x-2 mb-4">
              <Star className="text-gray-400 w-5 h-5 flex-shrink-0" />
              <span className="text-gray-400 font-semibold">Filter by Difficulty</span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {complexities.map((complexity) => (
                <button
                  key={complexity}
                  onClick={() => setSelectedComplexity(complexity)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    selectedComplexity === complexity
                      ? 'bg-orange-500 text-white scale-105'
                      : 'bg-zinc-900 text-gray-400 hover:bg-zinc-800 border border-white/10'
                  }`}
                >
                  {complexity}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-6 text-center">
          <p className="text-gray-400">
            Showing <span className="text-orange-500 font-semibold">{filteredRecipes.length}</span> of {recipes.length} automations
          </p>
        </div>

        {/* Recipe Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredRecipes.map((recipe) => (
            <div
              key={recipe.id}
              className="group bg-zinc-900/50 backdrop-blur-sm border border-white/5 rounded-xl p-6 hover:border-orange-500/50 transition-all duration-300 hover:transform hover:scale-105"
            >
              {/* Icon, Badge, and V3.0 Tag */}
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-lg flex items-center justify-center">
                  <div className="text-orange-500">
                    {getIcon(recipe.icon)}
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2">
                  {recipe.v3 && (
                    <span className="px-2 py-1 bg-orange-500/20 text-orange-500 text-xs font-bold rounded border border-orange-500/30">
                      V3.0
                    </span>
                  )}
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getComplexityColor(recipe.complexity)}`}>
                    {recipe.complexity}
                  </span>
                </div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-orange-500 transition-colors duration-200">
                {recipe.title}
              </h3>
              <p className="text-gray-400 text-sm mb-3 leading-relaxed">
                {recipe.description}
              </p>

              {/* Category Tag */}
              <span className="inline-block px-2 py-1 bg-zinc-800 text-gray-300 text-xs rounded mb-3">
                {recipe.category}
              </span>

              {/* Example Command */}
              <div className="bg-black/40 rounded-lg p-3 mb-3 border border-white/5">
                <p className="text-gray-500 text-xs mb-1">Example:</p>
                <p className="text-orange-400 text-sm font-mono">{recipe.example}</p>
              </div>

              {/* Time Savings */}
              {recipe.saves && (
                <div className="flex items-center space-x-2 mb-3">
                  <Clock className="w-4 h-4 text-green-500" />
                  <span className="text-green-500 text-sm font-semibold">{recipe.saves}</span>
                </div>
              )}

              {/* Commands List */}
              <div className="mb-4">
                <p className="text-gray-500 text-xs mb-2">Voice Commands:</p>
                <div className="space-y-1">
                  {recipe.commands.slice(0, 2).map((command, idx) => (
                    <div key={idx} className="flex items-center space-x-2 text-sm">
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                      <span className="text-gray-400">{command}</span>
                    </div>
                  ))}
                  {recipe.commands.length > 2 && (
                    <div className="flex items-center space-x-2 text-sm">
                      <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                      <span className="text-gray-500">+{recipe.commands.length - 2} more</span>
                    </div>
                  )}
                </div>
              </div>

              {/* CTA */}
              <button className="w-full flex items-center justify-center space-x-2 text-orange-500 hover:text-orange-400 font-medium transition-colors duration-200 py-2 bg-orange-500/10 hover:bg-orange-500/20 rounded-lg border border-orange-500/20">
                <span>Try This Command</span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </button>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredRecipes.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-400 text-lg mb-4">
              No automations found matching your search.
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedCategory('All');
                setSelectedComplexity('All');
              }}
              className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-20 text-center bg-zinc-900/50 border border-white/5 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">
            Can't find what you need?
          </h2>
          <p className="text-gray-400 mb-6">
            Create your own custom automation with our Python SDK. Executes locally with full system access.
          </p>
          <a
            href="/docs"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white font-semibold rounded-lg hover:shadow-lg hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105"
          >
            <span>View Documentation</span>
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>

        {/* Honesty Note */}
        <div className="mt-8 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/20 rounded-xl p-6 text-center">
          <p className="text-gray-300">
            <strong className="text-orange-500">How It Works:</strong> All automations execute locally on your machine. 
            Voice processing happens securely via encrypted cloud (OpenAI) with 300-500ms latency. 
            Your browsing data and files never leave your computer.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Recipes;